import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { MapPin, Car, ArrowRight } from "lucide-react";
import EditableText from "@/components/editor/EditableText";
import { cdn, srcSet } from "@/lib/img";

const ROAD_IMG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/cbvjhcfc_image.png";

export default function RoadIn() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="road-in" data-testid="road-in-section" className="wc-section bg-wc-cream" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">The Road In</p>
          <EditableText contentKey="roadin_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-8 max-w-2xl"
            defaultValue="The drive is part of the story." />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-6"
          >
            <EditableText contentKey="roadin_p1" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="Getting here is half the experience. You'll leave the highway and drive out along Digby Neck — a narrow peninsula that stretches into the Bay of Fundy. The towns get smaller. The trees get thicker. The pavement eventually gives way to gravel." />
            <EditableText contentKey="roadin_p2" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="The access road winds through dense Nova Scotia forest — spruce, birch, and fir pressing in on both sides. It's quiet. You might see a deer. Then the road opens up, and you're at Whale Cove." />
            <EditableText contentKey="roadin_p3" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="This isn't the kind of place you stumble onto. You have to want to come here. That's exactly the point." />

            <div className="bg-white/60 border border-wc-driftwood/30 rounded-md p-5 space-y-3">
              <h4 className="font-display text-wc-forest text-base font-semibold flex items-center gap-2">
                <Car className="w-4 h-4 text-wc-teal" /> What to Know
              </h4>
              <ul className="font-body text-wc-slate text-sm space-y-2">
                <li>Gravel access road — decent clearance recommended for larger rigs</li>
                <li>Standard RVs and trailers do fine. Contact us about specific lengths.</li>
                <li>Cell service is limited — plan accordingly (that's part of the appeal)</li>
                <li>Allow extra time — it's not about getting there fast</li>
              </ul>
            </div>
          </motion.div>

          {/* Road photo + route map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mb-8"
          >
            <div className="rounded-sm overflow-hidden aspect-[16/9]">
              <img
                src={cdn(ROAD_IMG, 1200)}
                srcSet={srcSet(ROAD_IMG)}
                sizes="100vw"
                alt="The entrance to Whale Cove Off Grid from Whale Cove Road — gravel access road winding into the forest, Digby Neck NS"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img"
              />
            </div>
            <p className="font-accent italic text-wc-slate/50 text-sm mt-2">
              The entrance off Whale Cove Road — where the gravel meets the forest
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="bg-wc-forest rounded-md p-8 text-wc-cream">
              <h4 className="font-display text-wc-brass text-lg font-semibold mb-8">Your Route</h4>
              <div className="space-y-6">
                {[
                  { label: "Digby", desc: "Groceries, fuel, last stop for essentials", km: "" },
                  { label: "Highway 217", desc: "Along Digby Neck peninsula", km: "~40 min" },
                  { label: "Access Road", desc: "Gravel road through the forest", km: "~5 min" },
                  { label: "Whale Cove — Site 1", desc: "You've arrived.", km: "" },
                ].map((stop, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-wc-teal/20 border border-wc-teal flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-wc-teal" />
                      </div>
                      {i < 3 && (
                        <div className="w-px h-8 bg-wc-teal/30 mt-1" />
                      )}
                    </div>
                    <div className="pt-1">
                      <div className="flex items-center gap-3">
                        <span className="font-display text-wc-cream font-semibold">{stop.label}</span>
                        {stop.km && (
                          <span className="font-body text-wc-driftwood/50 text-xs">{stop.km}</span>
                        )}
                      </div>
                      <p className="font-body text-wc-driftwood/70 text-sm mt-0.5">{stop.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-wc-teal/20">
                <a
                  href="https://www.google.com/maps/place/Digby+Neck,+NS/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-wc-teal text-sm flex items-center gap-2 hover:text-wc-brass transition-colors"
                  data-testid="google-maps-link"
                >
                  View Digby Neck on Google Maps <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
